// ─────────────────────────────────────────────────────────────────────────────
// sw-update.js  —  Service Worker update lifecycle + startup sequencer
//
// Handles every startup scenario in the correct order:
//
//   FIRST INSTALL   → SW installs & activates immediately (no prompt, no notes)
//                     → login
//
//   NO NEW VERSION  → login directly
//
//   NEW VERSION,
//   user picks NOW  → skipWaiting → page reloads → show release notes → login
//
//   NEW VERSION,
//   user picks LATER → login directly; new SW activates on next cold start
//
//   NEXT COLD START → new SW already active → show release notes → login
//   (after a "later")
//
// USAGE
// ─────
//   import { startApp } from "./sw-update.js";
//
//   startApp({
//     swUrl:        "/sw.js",
//     updatePrompt: document.getElementById("myUpdatePrompt"),   // <update-prompt>
//     releaseNotes: document.getElementById("myReleaseNotes"),   // <release-notes>
//     mandatory:    false,
//     getReleaseNotes: async (version) => {                      // fetch your notes
//       const r = await fetch(`/api/release-notes/${version}`);
//       return r.json();   // [{ category, items }]
//     },
//     onLogin: () => { /* start Firebase auth */ }
//   });
//
// SESSIONstorage KEYS  (managed internally, do not reuse these names)
//   _sw_show_notes        "1" when the page reloads after skipWaiting
//   _sw_notes_version     version string to show after that reload
//   _sw_last_version      version of the SW that was active last session
// ─────────────────────────────────────────────────────────────────────────────

import { CoffeeInfo, COFFEE_INFO_KEY } from "/src/info.js";

const NOTES_FLAG_KEY = "COFFEE_SW_SHOW_NOTES";
const NOTES_VER_KEY  = "COFFEE_SW_NOTES_VERSION";
export const LAST_VER_KEY   = "COFFEE_SW_LAST_VERSION";

// ── Public entry point ───────────────────────────────────────────────────────
export async function startApp({
  swUrl            = "/sw.js",
  updatePrompt,           // <update-prompt> element
  releaseNotes,           // <release-notes> element
  mandatory        = false,
  getReleaseNotes  = async () => [],   // (version: string) => Promise<Section[]>
  onLogin          = () => {},
  showVersion      = () => {},
  checkInterval    = 0,
} = {}) {

  // ── Step 1: Did we just reload after the user accepted an update? ─────────
  // skipWaiting() → controllerchange → window.location.reload() lands here.
  // Show release notes first, then login. Skip all SW detection.
  const pendingVersion = sessionStorage.getItem(NOTES_VER_KEY);
  if (sessionStorage.getItem(NOTES_FLAG_KEY) === "1" && pendingVersion) {
    console.log("💾 Step 1: Did we just reload after the user accepted an update?");
    sessionStorage.removeItem(NOTES_FLAG_KEY);
    sessionStorage.removeItem(NOTES_VER_KEY);
    localStorage.setItem(LAST_VER_KEY, pendingVersion);
    showVersion(pendingVersion);
    await _showReleaseNotes(releaseNotes, pendingVersion, getReleaseNotes);
    onLogin("STEP_1");
    // Register SW again for future update detection (fire-and-forget)
    _registerAndWatch({ swUrl, updatePrompt, releaseNotes, mandatory,
                        getReleaseNotes, onLogin, checkInterval });
    return;
  }

  // ── Step 2: No SW support → just login ───────────────────────────────────
  if (!("serviceWorker" in navigator)) {
    console.log("💾 Step 2: No SW support → just login");
    onLogin("STEP_2");
    return;
  }

  // ── Step 3: Register (or reuse) the SW ───────────────────────────────────
  let registration;
  try {
    console.log("💾 Step 3: Register (or reuse) the SW");
    registration = await navigator.serviceWorker.register(swUrl, {
        scope: import.meta.env.VITE_SCOPE,
        updateViaCache: 'none' //avoid serviceWorker to be cached
    });
  } catch (err) {
    console.error("[sw-update] Registration failed:", err);
    onLogin("STEP_3 (error)");
    return;
  }


  // ── Step 4: First install detection ─────────────────────────────────────
  // A true first install means there is no active SW at all yet — only an
  // installing (or at most waiting) one. We distinguish this from a hard
  // refresh or a new tab, where controller is null but an active SW exists.
  //
  // Conditions that must ALL be true for a first install:
  //   • no active SW in the registration  (nothing has ever activated)
  //   • an installing SW is present right now
  //   • no version stored yet (guarantees we haven't been here before)
  const isFirstInstall = !registration.active
                      && !!(registration.installing || registration.waiting)
                      && !localStorage.getItem(LAST_VER_KEY);
 
  if (isFirstInstall) {
    console.log("💾 Step 4: First install detection");
    await _waitForActivation(registration);
    let versionInfo = await _queryVersion(registration.active);
    if (versionInfo.version !== "unknown") {
        localStorage.setItem(LAST_VER_KEY, versionInfo.version);
        showVersion(versionInfo.version);
    }
    // Show release notes on first install, then login
    await _showReleaseNotes(releaseNotes, versionInfo.version, getReleaseNotes);
    onLogin("STEP_4");
    _watchForFutureUpdates({ registration, updatePrompt, checkInterval });
    return;
  }

  // ── Step 5: Check whether a "later" update activated between sessions ─────
  // The browser activates a waiting SW automatically when all tabs are closed.
  // We detect this by comparing the active SW version with what we stored.
  const versionInfo = await _queryVersion(registration.active);
  const activeVersion = versionInfo.version;
  const lastVersion   = localStorage.getItem(LAST_VER_KEY);
  console.log("💾 activeVersion=%s - lastVersion=%s", activeVersion, lastVersion);
  if (activeVersion && activeVersion !== "unknown" &&
      lastVersion   && lastVersion   !== "unknown" &&
      activeVersion !== lastVersion) {
    console.log("💾 Step 5: Check whether a 'later' update activated between sessions");
    // A new version activated naturally (cold-start after "later")
    localStorage.setItem(LAST_VER_KEY, activeVersion);
    showVersion(activeVersion);
    await _showReleaseNotes(releaseNotes, activeVersion, getReleaseNotes);
    onLogin("STEP_5");
    _watchForFutureUpdates({ registration, updatePrompt, checkInterval });
    return;
  }

  // Keep the stored version current
  if (activeVersion && activeVersion !== "unknown") {
    localStorage.setItem(LAST_VER_KEY, activeVersion);
    showVersion(activeVersion);
  }

  // ── Step 6: Is there already a waiting SW? ────────────────────────────────
  // Can happen if the user refreshed the tab after a new SW installed.
  if (registration.waiting) {
    console.log("💾 Step 6: Is there already a waiting SW?");
    _onWaitingFound({
      waitingSW: registration.waiting,
      updatePrompt, onLogin
    });
    _watchForFutureUpdates({ registration, updatePrompt, checkInterval });
    return;
    // onLogin will be called by the prompt flow, not here
  }

  // ── Step 7: No update pending → login directly ───────────────────────────
  onLogin("STEP_7");
  _watchForFutureUpdates({ registration, updatePrompt, checkInterval });
}

// ── Register + watch (used on the post-reload fast-path) ─────────────────────
async function _registerAndWatch(opts) {
  try {
    console.log("💾 Registering service worker...");
    const registration = await navigator.serviceWorker.register(opts.swUrl);
    _watchForFutureUpdates({ registration, ...opts });
  } catch { /* ignore */ }
}

// ── Watch for new SWs that arrive while the page is already open ──────────────
function _watchForFutureUpdates({ registration, updatePrompt, checkInterval }) {
  registration.addEventListener("updatefound", () => {
    console.log("💾 Updatefound...");
    const installing = registration.installing;
    if (!installing) {
        console.log("💾 !installing");
        return;
    }
    installing.addEventListener("statechange", () => {
      if (installing.state === "installed" && navigator.serviceWorker.controller) {
      //if (installing.state === "installed") {
        console.log("💾 Installed...");
        // A new SW finished installing and is now waiting
        _onWaitingFound({
          waitingSW: installing,
          updatePrompt, 
          // User is already past login at this point; after they choose,
          // if they pick "now" the page reloads and shows notes before login.
          // If they pick "later" nothing needs to happen.
          onLogin: () => {},
        });
      }
    });
  });

  // After skipWaiting the controller changes → reload
  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    console.log("💾 Controllerchange...");
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });

  /*
  if (checkInterval > 0) {
    setInterval(() => registration.update().catch(() => {}), checkInterval);
  }
  */
  checkInterval(registration)
}

// ── A new waiting SW was found — show the update prompt ──────────────────────
async function _onWaitingFound({ waitingSW, updatePrompt, onLogin }) {
  
  console.log("💾 A new waiting SW was found — show the update prompt...");
  const versionInfo = await _queryVersion(waitingSW);

  if (!updatePrompt) {
    // No prompt configured → activate silently (e.g. background tab)
    sessionStorage.setItem(NOTES_FLAG_KEY, "1");
    sessionStorage.setItem(NOTES_VER_KEY,  versionInfo.version);
    _activateWaiting(waitingSW);
    return;
  }

  updatePrompt.version = versionInfo.version;
  updatePrompt.mode    = versionInfo.upgrade;
  updatePrompt.show();

  const onAccept = () => {
    cleanup();
    // Set the flag so the post-reload startup knows to show release notes
    sessionStorage.setItem(NOTES_FLAG_KEY, "1");
    sessionStorage.setItem(NOTES_VER_KEY,  versionInfo.version);
    _activateWaiting(waitingSW);
    // Page reloads via controllerchange → window.location.reload()
  };

  const onDefer = () => {
    cleanup();
    onLogin("DEFER UPDATE"); // proceed to login without updating
  };

  const cleanup = () => {
    updatePrompt.removeEventListener("update-accepted", onAccept);
    updatePrompt.removeEventListener("update-forced",   onAccept);
    updatePrompt.removeEventListener("update-deferred", onDefer);
  };

  updatePrompt.addEventListener("update-accepted", onAccept);
  updatePrompt.addEventListener("update-forced",   onAccept);
  updatePrompt.addEventListener("update-deferred", onDefer);
}

// ── Tell the waiting SW to skip waiting ──────────────────────────────────────
function _activateWaiting(sw) {
  console.log("💾 Tell the waiting SW to skip waiting...");
  sw.postMessage({ type: "SKIP_WAITING" });
}

// ── Ask any SW for its version via MessageChannel (600 ms timeout) ────────────
function _queryVersion(sw) {
  console.log("💾 Ask any SW for its version via MessageChannel (600 ms timeout)...");
  return new Promise(resolve => {
    if (!sw) return resolve({ type: "COFFEE_VER", version: "unknown", upgrade: "unknown"});
    const channel = new MessageChannel();
    const timer   = setTimeout(() => resolve({ type: "COFFEE_VER", version: "unknown", upgrade: "unknown"}), 600);
    channel.port1.onmessage = (e) => {
      console.log("💾 Version received (%o)", e.data);
      clearTimeout(timer);
      //resolve(e.data?.version ?? "unknown");
      resolve(e.data);
    };
    channel.port1.start();
    try {
      sw.postMessage({ type: "GET_VERSION" }, [channel.port2]);
    } catch {
      clearTimeout(timer);
      resolve({ type: "COFFEE_VER", version: "unknown", upgrade: "unknown"});
    }
  });
}

// ── Wait until a freshly installing SW reaches "activated" ────────────────────
function _waitForActivation(registration) {
  console.log("💾 Wait until a freshly installing SW reaches 'activated'");
  return new Promise(resolve => {
    const sw = registration.installing || registration.waiting || registration.active;
    if (!sw || sw.state === "activated") {
        console.log("💾 SW activated (no wait)'");
        return resolve();
    }
    sw.addEventListener("statechange", function handler() {
      if (sw.state === "activated") {
        console.log("💾 SW activated (wait)");
        sw.removeEventListener("statechange", handler);
        resolve();
      }
    });
  });
}

// ── Show the release-notes sheet and resolve when the user dismisses it ───────
function _showReleaseNotes(releaseNotesEl, version, getReleaseNotes) {
  console.log("💾 Show the release-notes sheet and resolve when the user dismisses it");
  return new Promise(async resolve => {
    if (!releaseNotesEl) return resolve();

    releaseNotesEl.mode    = "onboarding";
    releaseNotesEl.version = version;
    releaseNotesEl.last = "Ok";
    releaseNotesEl.open();
    releaseNotesEl.onDone  = () => {        
        console.log("Onboarding complete — navigate to app");

        //let coffee = new CoffeeInfo();
        //let info = coffee.GetKey(COFFEE_INFO_KEY);
        //if ((Object.keys(info).length === 0) || (info == undefined)) {
        //    info = { "ver": version };
        //} else {
        //    info.ver = version;
        //}
        //coffee.SetKey(COFFEE_INFO_KEY, info);
        resolve();
    };

    //const onDismiss = () => {
    //  releaseNotesEl.removeEventListener("release-notes-dismissed", onDismiss);
    //  resolve();
    //};
    //releaseNotesEl.addEventListener("release-notes-dismissed", onDismiss);
  });
}

